import styled from 'styled-components'

import React from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'
import { Box, Button, Flex, Text, SrOnly, getPaletteColor } from 'pcln-design-system'
import { PricelineLogoNR } from '@pcln/brand-logos'
import { VipIcon } from '@pcln/vip'
import DisplayPrice from './DisplayPrice'
import StrikeThroughPrice from './StrikeThroughPrice'
import SavingsPercentage from './SavingsPercentage'
import PricingDisclosure from './PricingDisclosure'

const ViewFlightButton = styled(Button)`
  display: none;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSizes[1]}px;
  font-family: inherit;
  font-weight: 500;
  padding: 8px;
  height: 40px;
  line-height: 1;
  margin: 2px 0 4px 0;
  width: 104px;

  &:hover {
    background-color: ${(props) => getPaletteColor(props.hoverColor)(props)};
  }

  ${(props) => props.theme.flyQueries.desktoplg} {
    display: inline;
  }
`

const PriceFlex = styled(Flex)`
  align-content: flex-end;
  justify-content: flex-end;
  ${(props) => `${props.isExpressDeal ? props.theme.mediaQueries.sm : props.theme.flyQueries.tablet} {
      align-content: center;
      justify-content: center;
    }
  `}
  position: relative;
`

const StyledPriceBox = styled(Box)`
  text-align: right;
  line-height: 1.1;
  background-color: ${getPaletteColor('background.lightest')};
  padding-left: '8px';
  align-self: center;
  margin-left: 0;

  ${(props) => `${props.isExpressDeal ? props.theme.mediaQueries.sm : props.theme.flyQueries.tablet} {
      text-align: center;
    }
  `}
`

const TripTypeText = styled(Text)`
  font-size: 10px;
  ${(props) => props.theme.flyQueries.tablet} {
    font-size: 12px;
  }
`

export default function PriceBox(props, context) {
  const {
    priceInfo,
    isExpressDeal,
    isCugEligible,
    listingIsLoading,
    outBoundFlight,
    isRTSliceBySlice,
    srText,
    hideCtaButton,
    applyVIPDiscount,
  } = props

  const priceColor = isExpressDeal ? 'primary' : 'pricePrimary'
  const buttonColor = 'secondary'
  const hoverColor = isExpressDeal ? 'priceSecondary.base' : ''
  /* CUG rates are passed in as strikethrough, but that's only for retail.
  the pricedItin will have a 'comparativeTotalPrice' member  */
  let strikethrough = props.strikethrough ? props.strikethrough : 0
  /* Since we don't want the strikethrough on express details check for a
    'comparativeRetailPrice' member (notice the difference) */
  strikethrough =
    priceInfo && priceInfo.comparativeRetailPrice && priceInfo.comparativeRetailPrice > priceInfo.totalFare
      ? 0
      : strikethrough
  /* This is the original logic for strikethrough that was commented out on 7/27 because
      SOPQ net fares were showing the strikethrough.
  * SOPQ net fares are fares from Packages that the BE sends to us as Express Deals
  let strikethrough = props.strikethrough ? props.strikethrough :
  (priceInfo && priceInfo.comparativeRetailPrice &&
    priceInfo.comparativeRetailPrice > priceInfo.totalFare) &&
    priceInfo.comparativeRetailPrice || null;
  */

  const isCUGDiscount = !!strikethrough
  const currencyCode = props.priceDisplay.currencyCode || 'USD'
  const { price } = props.priceDisplay
  let savings = props.priceInfo && props.priceInfo.savings && props.priceInfo.savings

  if (isCUGDiscount) {
    const { cents, dollar } = props.priceDisplay
    const baseFare = parseFloat(`${dollar.replace(/\$|,/g, '')}.${cents}`)

    savings = baseFare
      ? {
          inPercent: Math.floor(((strikethrough - baseFare) / strikethrough) * 100),
          inDollars: Math.floor(strikethrough - baseFare),
        }
      : savings
  }

  // 'View Details' is the default button name
  const btnName = outBoundFlight ? 'Select' : 'View Details'

  // for PKG
  // if an override for the CTA label is available, use it
  const buttonName = (props.isExpressDeal && context.CTAButtonTextExd) || context.CTAText || btnName

  // AIR_VIP_SOPQ_DISCOUNT_MVP
  let newPriceInfo = props.priceDisplay
  if (applyVIPDiscount) {
    const { totalFare } = priceInfo
    // TODO: once issue is fixed with basePrice from priceInfo, subtract (baseFare * 0.05) from vipPrice
    const vipPrice = totalFare.toFixed(2)
    // newPriceInfo doesn't require dollars and cents keys like in props.priceDisplay.
    // The price-formatter used checks for price first, and only uses dollar
    // and cents keys if price is missing.
    newPriceInfo = { price: vipPrice }
  }
  // AIR_VIP_SOPQ_DISCOUNT_MVP --- END

  return listingIsLoading ? (
    <StyledPriceBox data-test='price-box' />
  ) : (
    <StyledPriceBox data-test='price-box' isExpressDeal={isExpressDeal}>
      {!!strikethrough && (
        <StrikeThroughPrice
          price={strikethrough}
          currencyCode={currencyCode}
          data-testid='strike-through-price-cug'
        />
      )}

      {applyVIPDiscount && (
        <Flex flexDirection='column' alignItems={['flex-end', 'center']}>
          <Flex alignItems='center' justifyContent='center' mb={2} data-testid='exd-vip-pricebox-icons'>
            <PricelineLogoNR />
            <VipIcon fontSize='24px' variation='vip' ml={2} />
          </Flex>
        </Flex>
      )}

      <PriceFlex isExpressDeal={isExpressDeal} applyVIPDiscount={applyVIPDiscount}>
        <DisplayPrice
          prepend={
            props.incrementalPrice && props.incrementalPrice.isNegative
              ? '-'
              : props.incrementalPrice && !props.incrementalPrice.isNegative
              ? '+'
              : null
          }
          color={priceColor}
          price={get(props, 'incrementalPrice.price') || newPriceInfo}
          // show cents for incremental price only when dollar amount is 0
          omitCents={!isEmpty(props.incrementalPrice) && props.incrementalPrice.price.dollar !== '$0'}
        />
      </PriceFlex>

      {context.pricingDisclosure ? (
        <PricingDisclosure disclosure={context.pricingDisclosure} />
      ) : (
        <div className='price-spacer' />
      )}

      {isRTSliceBySlice && (
        <TripTypeText color={'text.light'} data-test='trip-type'>
          {'Roundtrip'}
        </TripTypeText>
      )}

      {!hideCtaButton && (
        <ViewFlightButton
          color={buttonColor}
          data-test='button-view-flight'
          btnName={buttonName}
          hoverColor={hoverColor}
        >
          {buttonName}
          {/* SRonly text for View Details */}
          {buttonName === 'View Details' && (
            <SrOnly>
              {isExpressDeal ? `Click to view express deal for $${price}` : `$${price}, ${srText}`}
            </SrOnly>
          )}
        </ViewFlightButton>
      )}

      {!!savings && !isExpressDeal && !isCugEligible && 
        <SavingsPercentage savings={savings} />
      }
    </StyledPriceBox>
  )
}

PriceBox.defaultProps = {
  isSaleEligible: false,
  listingIsLoading: false,
  isRTSliceBySlice: false,
  hideCtaButton: false,
}

PriceBox.propTypes = {
  priceInfo: PropTypes.object,
  strikethrough: PropTypes.number,
  incrementalPrice: PropTypes.shape({
    isNegative: PropTypes.bool,
    price: PropTypes.shape({
      dollar: PropTypes.string,
      cents: PropTypes.string,
    }),
  }),
  seatsAvailable: PropTypes.number,
  priceDisplay: PropTypes.shape({
    dollar: PropTypes.string,
    cents: PropTypes.string,
    currencyCode: PropTypes.string,
    price: PropTypes.string,
  }).isRequired,
  isExpressDeal: PropTypes.bool,
  isCugEligible: PropTypes.bool,
  listingIsLoading: PropTypes.bool,
  outBoundFlight: PropTypes.bool,
  isRTSliceBySlice: PropTypes.bool,
  minimumRetailPrice: PropTypes.number,
  srText: PropTypes.string,
  hideCtaButton: PropTypes.bool,
  applyVIPDiscount: PropTypes.bool,
}

PriceBox.contextTypes = {
  SETI: PropTypes.object,
  CTAText: PropTypes.string,
  CTAButtonTextExd: PropTypes.string,
  pricingDisclosure: PropTypes.string,
}
